import {
  DEFAULT_BACKGROUND_NOTIFICATION_PREFS,
  parseBackgroundNotificationPrefs,
  planBackgroundNotification,
  pruneNotifiedIds,
  type BackgroundNotificationPayload,
  type BackgroundNotificationPrefs,
} from '@nightshift/notifications';
import type { UiAlert } from '@nightshift/ui-types';

export { pruneNotifiedIds as pruneBackgroundNotified };

const SOUND_KEY = 'nightshift.sound';
const NOTIFIED_KEY = 'nightshift.background-notified';
export const BACKGROUND_NOTIFICATIONS_KEY = 'nightshift.background-notifications';

/**
 * Getting the operator's attention when they are not looking at this tab.
 *
 * The pilot's failure mode was not "the dashboard was wrong", it was "nobody was looking".
 * So the handover count leaves the page: it goes in the document title (visible on the tab
 * strip and in the taskbar), on the favicon as a coloured badge, and — if the operator opts
 * in — through a short chime. Those three are cheap and need no permission prompt. The
 * fourth path is opt-in: a durable Windows / browser Notification for Human Review and
 * failures, with quiet hours, so a minimised or backgrounded tab still reaches the operator.
 *
 * Sound defaults to off. An ops tool that makes noise without being asked gets muted at the
 * OS level, and then it makes no noise ever again — including the time it mattered.
 */
export function soundEnabled(): boolean {
  try {
    return window.localStorage.getItem(SOUND_KEY) === 'on';
  } catch {
    return false;
  }
}

export function setSoundEnabled(on: boolean): void {
  try {
    window.localStorage.setItem(SOUND_KEY, on ? 'on' : 'off');
  } catch {
    // Nothing to do; the toggle simply will not persist.
  }
}

/**
 * A short two-note chime, synthesised.
 *
 * No audio file: the offline constraint means no CDN, and shipping a wav for one sound is
 * silly when the Web Audio API can make a nicer one in a dozen lines. Two soft sine tones a
 * fifth apart, fast attack, gentle release — audible across a room, not startling.
 */
export async function chime(): Promise<void> {
  if (!soundEnabled()) return;
  try {
    const Ctor = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    const context = new Ctor();
    if (context.state === 'suspended') await context.resume();
    const now = context.currentTime;
    for (const [index, frequency] of [660, 990].entries()) {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = frequency;
      const start = now + index * 0.13;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.16, start + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.34);
      oscillator.connect(gain).connect(context.destination);
      oscillator.start(start);
      oscillator.stop(start + 0.36);
    }
    setTimeout(() => void context.close(), 900);
  } catch {
    // Autoplay policy, no output device, a locked-down profile: all fine, all silent.
  }
}

/** `(2) Nightshift — 2 waiting on you` in the tab strip. */
export function setTitleBadge(waiting: number, viewLabel: string): void {
  document.title = waiting > 0 ? `(${waiting}) Nightshift — ${viewLabel}` : `Nightshift — ${viewLabel}`;
}

let faviconLink: HTMLLinkElement | null = null;

/**
 * Draw the favicon, badged with the waiting count.
 *
 * Generated on a canvas rather than shipped as files, because the badge is *data* — it has to
 * show a number that changes. A static icon plus a title prefix would work, but the tab strip
 * is where a minimised window lives, and an amber dot there is spotted from across a desk.
 */
export function setFaviconBadge(waiting: number): void {
  try {
    const size = 64;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.fillStyle = '#0b0d10';
    roundRect(ctx, 0, 0, size, size, 8);
    ctx.fill();

    ctx.strokeStyle = waiting > 0 ? '#ffb424' : '#8f98a8';
    ctx.lineWidth = 4;
    roundRect(ctx, 6, 6, size - 12, size - 12, 6);
    ctx.stroke();

    ctx.fillStyle = waiting > 0 ? '#ffc95a' : '#cfd6e4';
    ctx.font = '700 34px Inter, system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('N', size / 2, size / 2 + 2);

    if (waiting > 0) {
      ctx.fillStyle = '#ffb424';
      ctx.fillRect(size - 26, 4, 22, 22);
      ctx.fillStyle = '#0b0d10';
      ctx.font = '700 16px Inter, system-ui, sans-serif';
      ctx.fillText(waiting > 9 ? '9+' : String(waiting), size - 15, 16);
    }

    if (!faviconLink) {
      faviconLink =
        document.querySelector<HTMLLinkElement>('link[rel="icon"]') ??
        document.createElement('link');
      faviconLink.rel = 'icon';
      if (!faviconLink.parentNode) document.head.appendChild(faviconLink);
    }
    faviconLink.href = canvas.toDataURL('image/png');
  } catch {
    // A canvas-less environment loses the badge and keeps the title prefix.
  }
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + width, y, x + width, y + height, radius);
  ctx.arcTo(x + width, y + height, x, y + height, radius);
  ctx.arcTo(x, y + height, x, y, radius);
  ctx.arcTo(x, y, x + width, y, radius);
  ctx.closePath();
}

function readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // A locked-down profile simply does not persist the preference.
  }
}

/** Parse a localStorage value (or its absence) into prefs. Defaults stay off. */
export function prefsFromStorageValue(raw: string | null): BackgroundNotificationPrefs {
  if (raw === null || raw === '') return { ...DEFAULT_BACKGROUND_NOTIFICATION_PREFS };
  return parseBackgroundNotificationPrefs(raw);
}

export function backgroundNotificationPrefs(): BackgroundNotificationPrefs {
  return prefsFromStorageValue(readStorage(BACKGROUND_NOTIFICATIONS_KEY));
}

export function setBackgroundNotificationPrefs(
  patch: Partial<BackgroundNotificationPrefs>,
): BackgroundNotificationPrefs {
  const next: BackgroundNotificationPrefs = { ...backgroundNotificationPrefs(), ...patch };
  writeStorage(BACKGROUND_NOTIFICATIONS_KEY, JSON.stringify(next));
  return next;
}

export function backgroundNotificationPermission(): NotificationPermission | 'unsupported' {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}

export async function requestBackgroundNotificationPermission(): Promise<boolean> {
  if (typeof Notification === 'undefined') return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  try {
    const result = await Notification.requestPermission();
    return result === 'granted';
  } catch {
    return false;
  }
}

/**
 * Raise a Windows / browser notification when the operator has opted in.
 *
 * The first snapshot is the caller's problem (do not pass it here). This function is the
 * permission + Notification API edge; the opt-in / dedupe / quiet rules live in core.
 */
export function showBackgroundNotification(
  payload: BackgroundNotificationPayload,
  onClick?: () => void,
): void {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  try {
    const notification = new Notification(payload.title, {
      body: payload.body,
      tag: payload.tag,
      silent: true,
    });
    notification.onclick = () => {
      window.focus();
      notification.close();
      onClick?.();
    };
  } catch {
    // A missing service, a policy block, an unsupported constructor: the in-page surfaces remain.
  }
}

/**
 * Alert ids this browser has already raised an OS notification for.
 *
 * Kept in localStorage, not just in memory: a reload used to forget them, so the next
 * genuinely-new snapshot could notify a second time for something the operator had already
 * been told about. Pruned to live alerts by `pruneBackgroundNotified` on every snapshot.
 */
export function readBackgroundNotified(): Set<string> {
  try {
    const raw = window.localStorage.getItem(NOTIFIED_KEY);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((item): item is string => typeof item === 'string'));
  } catch {
    return new Set();
  }
}

export function writeBackgroundNotified(ids: ReadonlySet<string>): void {
  try {
    window.localStorage.setItem(NOTIFIED_KEY, JSON.stringify([...ids]));
  } catch {
    // A full or blocked store only costs us the cross-reload memory.
  }
}

export function planAlertBackgroundNotification(
  alert: UiAlert,
  alreadyNotified: ReadonlySet<string>,
  now = new Date(),
): BackgroundNotificationPayload | null {
  return planBackgroundNotification(alert, {
    prefs: backgroundNotificationPrefs(),
    alreadyNotified,
    now,
    dashboardVisible: typeof document !== 'undefined' && document.visibilityState === 'visible',
    permissionGranted: backgroundNotificationPermission() === 'granted',
  });
}
